import { computed, unref, type MaybeRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQueryClient, useMutation, type UseQueryOptions } from '@tanstack/vue-query';

import { YardTaskStatus } from '@nova/core';

import { queryKeys, taskKeys } from '@satellite/composables/queryKeys';
import { useQueryHelpers } from '@/composables';
import yardTaskService from '@satellite/services/yard-tasks-service';
import { isDestinationSpotNotFoundError } from '@satellite/helpers/yardTaskErrors';
import useNotify from '@/composables/useNotify';

import type {
  IQueryArgs,
  ITask,
  ITaskListResponse,
  IServiceParams,
  IServiceOptions
} from '@/types/apiTypes';

const { useSmartQuery, useSmartMutation } = useQueryHelpers();

/**
 * Standalone mutation for updating a task (usable outside of the useTaskQueries factory).
 */
export function useUpdateTaskMutation(options?: {
  onSuccess?: (
    data: object,
    variables: {
      taskId: string;
      params: IServiceParams;
      options?: IServiceOptions;
    },
    context: unknown,
    queryClient: ReturnType<typeof useQueryClient>
  ) => void;
  onError?: (
    error: unknown,
    variables: {
      taskId: string;
      params: IServiceParams;
      options?: IServiceOptions;
    },
    context: unknown,
    queryClient: ReturnType<typeof useQueryClient>
  ) => void;
}) {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const { notify } = useNotify();
  return useMutation<
    object,
    unknown,
    {
      taskId: string;
      params: IServiceParams;
      options?: IServiceOptions;
    }
  >({
    mutationFn: ({ taskId, params, options }) =>
      yardTaskService.updateYardTask(taskId, params, options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.taskId) });
      await queryClient.invalidateQueries({ queryKey: taskKeys.list() });
      // Refresh inventory-check scope/counts on completion so they don't rely on the Subspace
      // round-trip (OD-3837 A4). Scoped to completion so unrelated task saves don't refetch.
      if (variables.params.status === YardTaskStatus.COMPLETED) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.yardInventoryCheck(variables.taskId)
        });
      }
      options?.onSuccess?.(data, variables, context, queryClient);
    },
    onError: (error, variables, context) => {
      if (isDestinationSpotNotFoundError(error)) {
        notify(t('yard.messages.destinationSpotNotFound'), 'error');
      }
      options?.onError?.(error, variables, context, queryClient);
    }
  });
}

/**
 * TODO: (welington) review the use of useSmart*
 */
export default function useTaskQueries() {
  const getTasksQuery = (queryArgs?: IQueryArgs) =>
    useSmartQuery<ITaskListResponse>(
      taskKeys.list(),
      ({ params, options, signal }) => {
        const opts = options || {};
        return yardTaskService.getYardTaskList({
          query: {
            fields: opts.fields,
            joins: opts.joins
          },
          getResponseConfig: {
            includeMetaData: opts.includeMetaData
          },
          requestConfig: {
            params,
            signal
          }
        }) as Promise<ITaskListResponse>;
      },
      queryArgs
    );

  const getTaskQuery = (
    taskId: MaybeRef<string>,
    queryArgs?: {
      params?: MaybeRef<IServiceParams>;
      options?: MaybeRef<IServiceOptions>;
      enabled?: MaybeRef<boolean>;
    } & Omit<UseQueryOptions<ITask>, 'queryKey' | 'queryFn' | 'enabled'>
  ) => {
    const unwrappedTaskId = computed(() => unref(taskId));
    return useSmartQuery<ITask>(
      computed(() => taskKeys.detail(unwrappedTaskId.value)),
      ({ options, signal }) =>
        yardTaskService.getYardTask(unwrappedTaskId.value, {
          query: options,
          requestConfig: { signal }
        }),
      queryArgs
    );
  };

  const useCreateTaskMutation = () =>
    useSmartMutation(
      ({ params, options }: { params: IServiceParams; options?: IServiceOptions }) =>
        yardTaskService.createYardTask(params, options),
      {
        onSuccess: (_data, _variables, _context, queryClient) => {
          void queryClient.invalidateQueries({ queryKey: taskKeys.list() });
        }
      }
    );

  return { getTasksQuery, getTaskQuery, useUpdateTaskMutation, useCreateTaskMutation };
}
